import { Download, OpenInNew } from '@mui/icons-material';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { ScrollMode } from '@react-pdf-viewer/core';
import DOMPurify from 'dompurify';
import { Form, Formik } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import ImagePanZoomRotate from 'react-image-pan-zoom-rotate';

import { downloadAsData, openFile } from '@shared/commonFunctions';
import { GridCell } from '@shared/components/grid/GridCell';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { PdfViewer } from '@shared/components/PdfViewer';
import { SERVICES } from '@shared/constants/apiConfig';
import { Loader } from '@shared/enhancers/withLoader';
// import { RadioButtonListField } from '@shared/fields/RadioButtonListField';
// import { TextInputField } from '@shared/fields/TextInputField';
import { SaveButton } from '@shared/inputs/Buttons';
import { HTMLFileWrapper, StipReviewGrid, StipReviewGridVertical, StyledNewTabButton } from '@styles/CommonStyles';
import { colorTheme } from '@styles/muiTheme';
import { ButtonWraper } from '@styles/StyledDiv';

import { useQueryState } from '@shared/hooks/useQueryState';
import { IStip, IStipsReviewProps } from '../interfaces';
import { requestUpdateStipsReview } from '../requests';
import { stipReviewValidationSchema } from '../validation';
import { StipReviewForm } from './StipReviewForm';

const imageExts = ['jpeg', 'jpg', 'png', 'gif'];
const previewNotAvailable = 'Preview not available';


export const StipsReview = ({ contactStipData, newTab = false, confidenceScore }: IStipsReviewProps) => {
  const {
    data: stipReview,
    mutate: reload,
    error: stipDataError,
    isValidating,
  } = useSWR<IStip>(`${SERVICES.DOCUMENT}/stips/${contactStipData.contactStipId}?contactId=${contactStipData.contactId}&excludeFileBinaryContent=${true}`, {
    fallbackData: { reviewStatusNote: '', reviewStatus: '' } as IStip,
    revalidateOnMount: true,
  });

  const [scoreFromQuery] = useQueryState('confidenceScore', 0);
  const confidenceScoreValue = useMemo(() => {
    const parsedConfidenceScore = Number(scoreFromQuery);
    const currentConfidenceScore = parsedConfidenceScore || confidenceScore || 0;

    return Number(currentConfidenceScore.toFixed(2));
  }, [confidenceScore, scoreFromQuery]);

  useEffect(() => {
    if (stipDataError) {
      Notistack.generalError(stipDataError?.response?.data?.message);
    }
  }, [stipDataError]);

  const extension = useMemo(() => {
    // This regex looks for a dot, then captures only letters/numbers
    // until it hits a space, a parenthesis, or the end of the line.
    // e.g. "document.pdf", "image.jpeg (Deleted)", "file.name.with.dots.txt"
    const match = contactStipData.fileName.match(/\.([a-z0-9]+)(?=[^a-z0-9]|$)/i);
    
    return match ? match[1] : contactStipData.fileName;
  }, [contactStipData.fileName]);

  const disableOpenInNew = useMemo(() => {
    const extraExts = ['pdf', 'html'];
    const availableExts = imageExts.concat(extraExts);

    return !availableExts.includes(extension.toLowerCase());
  }, [extension]);

  const handleSubmit = useCallback(async (data: IStip) => {
    const postData = { ...data, regOwners: data.regOwners ? String(data.regOwners) : data.regOwners };
    await requestUpdateStipsReview(postData, contactStipData.contactStipId);
    reload();
  }, []);

  const downloadFile = useCallback(() => {
    if (stipReview?.content || stipReview?.blobDownloadLink) {
      downloadAsData(stipReview.blobFile.name, stipReview.content, stipReview?.blobDownloadLink);
    }
  }, [stipReview]);

  const openInNewTab = useCallback(async () => {
    if (stipReview?.content || stipReview?.blobDownloadLink) {
      openFile(stipReview.content, extension, stipReview?.blobFile?.name, stipReview?.blobDownloadLink);
    }
  }, [stipReview, extension]);

  // Logic: Use blobDownloadLink if it exists, otherwise build the Base64 string
  const resolvedImage = useMemo(() => {
    if (stipReview?.blobDownloadLink) {
      return stipReview.blobDownloadLink;
    }
    
    if (stipReview?.content) {
      // You can use a default extension like 'png' if stipReview.extension is missing
      const ext = extension || 'png'; 
      return `data:image/${ext};base64,${stipReview.content}`;
    }

    return ''; // Fallback for when data is still loading
  }, [stipReview, extension]);

  const getHtml = useCallback((content = '') => {
    // Decode base64 content
    const html = atob(content);

    // sanitize html
    const sanitizedHtml = DOMPurify.sanitize(html?.trim());

    // Return an object with the __html property
    return { __html: sanitizedHtml };
  }, []);

  return isValidating && !stipDataError ? (
    <Loader />
  ) : (
    <Grid
      container
      columnSpacing={{ xs: 1, sm: 2, md: 3 }}
    >
      <Grid
        item
        marginLeft={2}
        xs={newTab ? 4 : 5.7}
      >
        <Grid
          container
          pt={2}
          spacing={1}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Grid
            item
            md={2}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography>File Name:</Typography>
          </Grid>
          <Grid
            item
            md={8}
            sx={{ display: 'flex', alignItems: 'left' }}
          >
            <GridCell
              text={stipReview?.blobFile?.name}
              sx={{ color: colorTheme.palette.primary.main }}
            />
          </Grid>
          <Grid
            item
            md={2}
          >
            <Stack
              direction={'row'}
              spacing={1}
            >
              <StyledNewTabButton
                variant="contained"
                disabled={disableOpenInNew}
                startIcon={<OpenInNew />}
                onClick={() => openInNewTab()}
              />
              <StyledNewTabButton
                variant="contained"
                startIcon={<Download />}
                onClick={() => downloadFile()}
              />
            </Stack>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          pt={2}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Grid item>
            <Typography>File Type:</Typography>
          </Grid>
          <Grid item>
            <GridCell
              text={contactStipData?.stipType}
              sx={{ color: colorTheme.palette.primary.main }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          pt={2}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Grid item>
            <Typography>Confidence Score:</Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="subtitle1"
            >
              {`${confidenceScoreValue ?? '-'}%`}
            </Typography>
          </Grid>
        </Grid>
        <Formik
          enableReinitialize
          initialValues={{ ...stipReview, reviewStatusNote: stipReview?.reviewStatusNote || '' } as IStip}
          validationSchema={stipReviewValidationSchema}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <Form onSubmit={props.handleSubmit}>
              <Stack spacing={2} sx={{ marginTop: '3px' }}>
                <StipReviewForm stipReview={props.values} />
              {/* <RadioButtonWrapper>
                <FastField
                  name="reviewStatus"
                  options={radioOptions}
                  component={RadioButtonListField}                  
                />
              </RadioButtonWrapper>

              {(props.values?.reviewStatus === 'Denied' || props.values?.reviewStatus === 'ApprovedCondition') && (
                <FastField
                  name={'reviewStatusNote'}
                  label={props.values?.reviewStatus === 'Denied' ? 'Deny Reason' : 'Condition'}
                  type={'text'}
                  component={TextInputField}
                />
              )} */}

                <ButtonWraper>
                  <SaveButton
                    width="inherit"
                    // disabled={!stipReview?.id}
                    disabled={true}
                  />
                </ButtonWraper>
              </Stack>
            </Form>
          )}
        </Formik>
      </Grid>
      {extension.toLocaleLowerCase() === 'pdf' ? (
        <Grid
          item
          marginLeft={1.5}
          xs={newTab ? 7.5 : 5.8}
        >
          <PdfViewer
            scrollMode={ScrollMode.Horizontal}
            fileSource={stipReview?.blobDownloadLink || stipReview?.content}
          />
        </Grid>
      ) : imageExts.includes(extension.toLowerCase()) ? (
        <StipReviewGrid
          item
          xs={newTab ? 7.5 : 6}
        >
          <Box
            sx={{
              height: '80vh',
              overflow: 'hidden',
              '& div': {
                top: '7rem !important',
                right: '2rem !important'
              }
            }}
          >
            <ImagePanZoomRotate
              image={resolvedImage}
            />
          </Box>
        </StipReviewGrid>
      ) : extension.toLocaleLowerCase() === 'html' ? (
        <Grid
          item
          xs={newTab ? 7.5 : 6}
        >
          <HTMLFileWrapper>
            <Box dangerouslySetInnerHTML={getHtml(stipReview?.content || '')} />
          </HTMLFileWrapper>
        </Grid>
      ) : (
        <StipReviewGridVertical
          item
          xs={newTab ? 7.5 : 6}
        >
          <Typography variant="h2">{previewNotAvailable}</Typography>
        </StipReviewGridVertical>
      )}
    </Grid>
  );
};
